import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { JobRow } from "@gwrctl/api-client";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  CopyPlus,
  Flag,
  LayoutGrid,
  ListChecks,
  LogOut,
  Menu,
  MessageSquare,
  Inbox,
  Radio,
  MessageSquareWarning,
  RadioTower,
  Server,
  ShieldCheck,
  ShieldAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { client, clearSession } from "../lib/client.js";
import { useEventListener, useEvents } from "../lib/events.tsx";
import { jobTitle } from "../lib/labels.ts";
import { useToast } from "../lib/toast.tsx";
import { AppIcon } from "./AppIcon.tsx";
import { JobDock } from "./JobDock.tsx";
import { Dot } from "./Ui.tsx";

/**
 * Uygulama kabuğu: solda kenar çubuğu, sağda başlık + içerik. Dar ekranda kenar
 * çubuğu gizlenir, üst çubuktaki menü düğmesiyle çekmece olarak açılır.
 *
 * Kenar çubuğunun üstündeki agent kutusu bilinçli olarak her ekranda görünür —
 * bu sistemde "PC açık mı?" sorusu her işin ön koşulu.
 */

interface NavItem {
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Uygulamalar ikon yerine kendi logosunu gösterir. */
  appId?: string;
  meta?: string | number;
  metaTone?: "accent" | "danger";
}

const ACTIVE = ["queued", "claimed", "running"];

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);

  const { data: agents } = useQuery({
    queryKey: ["agents"],
    queryFn: () => client.agents(),
    refetchInterval: 20_000,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => client.me() });
  const { data: apps } = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });
  const { data: jobs } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => client.jobs({ limit: 60 }),
    refetchInterval: 30_000,
  });
  useLiveJobList();
  // Moderasyon rozeti gerçek sayıdan: açık (resolved=false) şikâyetler.
  // Uç kapalıysa (MOVIEPORT_DATABASE_URL yok) sessizce rozetsiz devam eder.
  const { data: modCounts } = useQuery({
    queryKey: ["moderation-counts"],
    queryFn: () => client.moderationCounts(),
    refetchInterval: 60_000,
    retry: false,
  });

  // Geri bildirim rozeti: onay bekleyen oyuncu gönderileri. Uç yoksa (eski
  // sunucu) sessizce rozetsiz.
  const { data: feedbackCounts } = useQuery({
    queryKey: ["feedback-counts"],
    queryFn: () => client.feedbackCounts(),
    refetchInterval: 60_000,
    retry: false,
  });

  useJobFinishedToasts(apps?.map((a) => ({ id: a.id, name: a.displayName })) ?? []);

  const active = jobs?.filter((j) => ACTIVE.includes(j.status)) ?? [];
  const failed = jobs?.filter((j) => j.status === "failed" && j.createdBy !== "watchdog").length ?? 0;

  // Sekme başlığında çalışan iş sayısı: başka sekmedeyken de görünsün.
  useEffect(() => {
    document.title = active.length ? `(${active.length}) GWR Control` : "GWR Control";
  }, [active.length]);

  // Sayfa değişince çekmece kapansın.
  useEffect(() => setDrawer(false), [location.pathname]);

  const sections: { head?: string; items: NavItem[] }[] = [
    {
      items: [
        { to: "/", label: "Pano", icon: LayoutGrid },
        {
          to: "/jobs",
          label: "İşler",
          icon: ListChecks,
          ...(active.length
            ? { meta: active.length, metaTone: "accent" as const }
            : failed
              ? { meta: failed, metaTone: "danger" as const }
              : {}),
        },
      ],
    },
    {
      head: "Uygulamalar",
      items: (apps ?? []).map((a) => ({ to: `/apps/${a.id}`, label: a.displayName, appId: a.id })),
    },
    {
      head: "Yönetim",
      items: [
        {
          to: "/moderation",
          label: "Moderasyon",
          icon: ShieldAlert,
          ...(modCounts?.total ? { meta: modCounts.total, metaTone: "accent" as const } : {}),
        },
        {
          to: "/feedback",
          label: "Geri bildirim",
          icon: MessageSquare,
          ...(feedbackCounts?.total ? { meta: feedbackCounts.total, metaTone: "accent" as const } : {}),
        },
        { to: "/analytics", label: "Kullanım", icon: BarChart3 },
        { to: "/notifications", label: "Bildirimler", icon: Bell },
        { to: "/flags", label: "Feature flag", icon: Flag },
        { to: "/stations", label: "İstasyonlar", icon: Radio },
        { to: "/station-duplicates", label: "Olası kopyalar", icon: CopyPlus },
        { to: "/stream-failures", label: "Yayın hataları", icon: RadioTower },
        { to: "/station-reports", label: "İstasyon şikayetleri", icon: MessageSquareWarning },
        { to: "/station-submissions", label: "İstasyon başvuruları", icon: Inbox },
      ],
    },
    {
      head: "Sistem",
      items: [
        { to: "/bot", label: "Discord botları", icon: Bot },
        { to: "/ops", label: "Sunucu", icon: Server },
        { to: "/security", label: "Güvenlik", icon: ShieldCheck },
      ],
    },
  ];

  const sidebar = (
    <>
      <div className="flex items-center gap-1.5 px-4 pt-[18px] pb-3.5 tracking-[-0.01em]">
        <img src="/app-icons/gwrlabs.png" alt="" aria-hidden className="mr-1 size-6 rounded-[6px]" />
        <span className="text-[15px] font-semibold">GWR</span>
        <span className="text-[15px] font-medium text-accent-400">Control</span>
        <button
          type="button"
          onClick={() => setDrawer(false)}
          aria-label="Menüyü kapat"
          className="ml-auto grid size-7 cursor-pointer place-items-center rounded-[6px] text-neutral-500 hover:bg-white/5 md:hidden"
        >
          <X size={16} />
        </button>
      </div>

      <AgentBox agents={agents} activeCount={active.length} />

      <nav aria-label="Ana menü" className="flex flex-col gap-px overflow-y-auto px-2 pb-3">
        {sections.map((section, i) => (
          <div key={section.head ?? i} className="flex flex-col gap-px">
            {section.head && (
              <div className="px-2.5 pt-4 pb-1.5 text-2xs font-medium tracking-[0.09em] text-neutral-600 uppercase">
                {section.head}
              </div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `group relative flex items-center gap-2.5 rounded-[6px] px-2.5 py-[7px] text-sm transition ${
                    isActive
                      ? "bg-accent-900 font-medium text-neutral-100"
                      : "text-neutral-400 hover:bg-white/[0.04] hover:text-neutral-200"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute top-1.5 bottom-1.5 left-0 w-[2px] rounded-[2px] bg-accent" />
                    )}
                    {item.appId ? (
                      <AppIcon id={item.appId} name={item.label} size={18} />
                    ) : (
                      item.icon && (
                        <item.icon
                          size={15}
                          strokeWidth={1.8}
                          className={isActive ? "text-accent-300" : "text-neutral-600 group-hover:text-neutral-400"}
                          aria-hidden
                        />
                      )
                    )}
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.meta != null && (
                      <span
                        className={`mono rounded-full px-1.5 text-2xs ${
                          item.metaTone === "danger"
                            ? "bg-danger/15 text-danger"
                            : "bg-accent-800/60 text-accent-200"
                        }`}
                      >
                        {item.meta}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="mt-auto flex items-center gap-2 border-t border-neutral-900 px-4 pt-3 pb-4 text-xs text-neutral-500">
        <span className="truncate" title="giriş yapılan hesap">
          {me?.email ?? "…"}
        </span>
        <button
          type="button"
          className="ml-auto flex cursor-pointer items-center gap-1 rounded-[6px] px-1.5 py-1 hover:bg-white/5 hover:text-neutral-200"
          onClick={() => {
            void client.logout().catch(() => {});
            clearSession();
            navigate("/login");
          }}
        >
          <LogOut size={13} aria-hidden />
          çıkış
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-full">
      {/* Masaüstü: sabit kenar çubuğu */}
      <aside
        className="sticky top-0 hidden h-screen w-[236px] shrink-0 flex-col border-r border-neutral-900 md:flex"
        style={{ background: "linear-gradient(180deg, #171927 0%, #12131c 60%)" }}
      >
        {sidebar}
      </aside>

      {/* Dar ekran: çekmece */}
      {drawer && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Menü">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawer(false)} />
          <aside
            className="animate-modal-in absolute top-0 bottom-0 left-0 flex w-[260px] flex-col border-r border-neutral-900"
            style={{ background: "#151722" }}
          >
            {sidebar}
          </aside>
        </div>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-neutral-900 bg-bg/90 px-4 py-2.5 backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => setDrawer(true)}
            aria-label="Menüyü aç"
            className="grid size-8 cursor-pointer place-items-center rounded-[6px] border border-neutral-800 text-neutral-300"
          >
            <Menu size={16} />
          </button>
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            <img src="/app-icons/gwrlabs.png" alt="" aria-hidden className="size-5 rounded-[5px]" />
            GWR <span className="font-medium text-accent-400">Control</span>
          </span>
          {active.length > 0 && (
            <span className="ml-auto flex items-center gap-1.5 text-xs text-accent-300">
              <Activity size={13} className="pulse" aria-hidden />
              {active.length} iş
            </span>
          )}
        </div>
        {children}
      </main>

      <JobDock jobs={jobs} apps={apps} />
    </div>
  );
}

function AgentBox({
  agents,
  activeCount,
}: {
  agents: { kind: string; name: string; online: boolean }[] | undefined;
  activeCount: number;
}): ReactNode {
  const { connected } = useEvents();
  return (
    <div className="mx-3 mb-2 grid gap-[7px] rounded-[8px] border border-neutral-900 bg-white/[0.02] px-3 py-2.5 text-xs">
      {(["pc", "vps"] as const).map((kind) => {
        const agent = agents?.find((a) => a.kind === kind);
        // Veri gelmediyse "kapalı" demek yanlış olur: bilinmiyor.
        const known = agents !== undefined;
        const online = agent?.online ?? false;
        return (
          <div key={kind} className="flex items-center gap-2 text-neutral-400">
            <Dot tone={!known ? "idle" : online ? "ok" : "danger"} glow={online} />
            <span className="text-neutral-200">{agent?.name ?? kind}</span>
            <span className="ml-auto">{!known ? "…" : online ? "açık" : "kapalı"}</span>
          </div>
        );
      })}
      <div
        className="flex items-center gap-2 border-t border-neutral-900 pt-[7px] text-neutral-400"
        title={connected ? "Canlı güncellemeler geliyor" : "Canlı bağlantı yok; sayfalar birkaç saniyede bir yenileniyor"}
      >
        <span
          className={`inline-block size-1.5 rounded-full ${connected ? "pulse" : ""}`}
          style={{ background: connected ? "var(--color-accent)" : "var(--color-warn)" }}
        />
        <span>canlı akış</span>
        <span className={`ml-auto ${connected ? "" : "text-warn"}`}>
          {!connected ? "bağlanıyor…" : activeCount ? `${activeCount} iş çalışıyor` : "bağlı"}
        </span>
      </div>
    </div>
  );
}

/**
 * İş listesini (kenar çubuğu sayacı + iş kutusu) canlı tutar. İlerleme olayları sık
 * gelir; her birinde listeyi yeniden çekmek yerine önbellekteki satır yamanır.
 * Durum değişimlerinde (yeni iş, başladı, bitti) liste tazelenir.
 */
function useLiveJobList(): void {
  const queryClient = useQueryClient();
  useEventListener((event) => {
    if (event.t !== "job.update") return;
    const cached = queryClient.getQueryData<JobRow[]>(["jobs"]);
    const row = cached?.find((j) => j.id === event.jobId);
    if (row && row.status === event.status && event.step !== undefined) {
      queryClient.setQueryData<JobRow[]>(["jobs"], (list) =>
        list?.map((j) =>
          j.id === event.jobId ? { ...j, step: event.step ?? j.step, pct: event.pct ?? null } : j,
        ),
      );
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ["jobs"] });
  });
}

/**
 * Başka bir sayfadayken biten işleri haber verir. Sağlık gözcüsünün 5 dakikalık
 * işleri ve ekranda zaten izlenen kısa işler bildirilmez: başarısız her iş, başarılı
 * olanlardan ise 30 sn'den uzun sürenler (derleme, yükleme).
 */
function useJobFinishedToasts(apps: { id: string; name: string }[]): void {
  const toast = useToast();
  const seen = useRef(new Set<string>());
  const appsRef = useRef(apps);
  appsRef.current = apps;

  useEventListener((event) => {
    if (event.t !== "job.update") return;
    if (!["succeeded", "failed"].includes(event.status)) return;
    if (seen.current.has(event.jobId)) return;
    seen.current.add(event.jobId);
    void client
      .job(event.jobId)
      .then(({ job }) => {
        if (job.createdBy === "watchdog") return;
        const took =
          job.startedAt && job.finishedAt
            ? new Date(job.finishedAt).getTime() - new Date(job.startedAt).getTime()
            : 0;
        if (job.status === "succeeded" && took < 30_000) return;
        const app = appsRef.current.find((a) => a.id === job.appId)?.name;
        const title = `${app ? `${app} · ` : ""}${jobTitle(job.type)}`;
        toast(
          job.status === "succeeded"
            ? { tone: "ok", title, body: "Tamamlandı.", link: { to: `/jobs/${job.id}`, label: "Kaydı aç" } }
            : {
                tone: "danger",
                title,
                body: job.error ?? "Başarısız oldu.",
                link: { to: `/jobs/${job.id}`, label: "Nedenini gör" },
              },
        );
      })
      .catch(() => {
        /* bildirim en iyi çaba; iş kaydı yine listede */
      });
  });
}

/** Sayfa başlığı — üstte küçük bağlam, altında sayfa adı, sağda eylemler. */
export function PageHeader({
  kicker,
  title,
  actions,
  meta,
}: {
  kicker?: string;
  title: ReactNode;
  actions?: ReactNode;
  /** Başlığın altındaki ikincil satır (durum, yol …). */
  meta?: ReactNode;
}): ReactNode {
  return (
    <header className="flex flex-wrap items-end gap-x-4 gap-y-3 border-b border-neutral-900 px-4 pt-5 pb-4 md:px-7">
      <div className="min-w-0">
        {kicker && <div className="kicker mb-1">{kicker}</div>}
        <h1 className="text-lg font-semibold tracking-[-0.02em]">{title}</h1>
        {meta && <div className="mt-1.5 text-xs text-neutral-500">{meta}</div>}
      </div>
      {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function PageBody({ children }: { children: ReactNode }): ReactNode {
  // minmax(0,1fr): sütun içeriğe göre büyümesin (varsayılan "auto" iz geniş bir
  // log/tablo yüzünden sayfayı yatayda taşırıyordu).
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 px-4 pt-5 pb-12 md:px-7">{children}</div>
  );
}
